import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { Education } from '../types';

interface EducationSectionProps {
  education: Education[];
}

const EducationSection = ({ education }: EducationSectionProps) => {
  return (
    <section className="bg-white border-2 border-[#0C2C39] shadow-[4px_4px_0px_0px_#0C2C39] rounded-[2rem] p-8 sm:p-12 mb-12 relative overflow-hidden">
      {/* Subtle Pattern Grid */}
      <div className="absolute inset-0 z-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(#0C2C39 0.5px, transparent 0.5px)',
          backgroundSize: '24px 24px'
        }} />

      <div className="relative z-10 mb-12">
        <h2 className="text-4xl sm:text-5xl font-black text-[#0C2C39] mb-4 tracking-tighter font-roboto">
          Academic Narrative
        </h2>
        <p className="text-[#0C2C39]/50 text-lg font-medium">Educational background and qualifications</p>
      </div>

      <div className="space-y-12 relative z-10">
        {education.map((edu, index) => (
          <div key={edu.id} className="relative">
            {/* Timeline Line */}
            {index < education.length - 1 && (
              <div className="absolute left-[27px] top-14 w-1 h-full bg-[#0C2C39]/10 rounded-full" />
            )}

            <div className="flex items-start space-x-8">
              {/* Timeline Dot & Logo */}
              <div className="relative flex-shrink-0">
                <div className="w-14 h-14 bg-white border-2 border-[#0C2C39] rounded-2xl flex items-center justify-center shadow-[2px_2px_0px_0px_#0C2C39] relative z-10">
                  <Icon name="GraduationCap" size={24} className="text-[#0C2C39]" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 neubrutal-card p-8 bg-white group hover:shadow-[6px_6px_0px_0px_#0C2C39]">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6 gap-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-black text-[#0C2C39] tracking-tight mb-2 group-hover:text-teal-700 transition-colors">
                      {edu.degree}
                    </h3>
                    <p className="text-sm font-black text-teal-700 uppercase tracking-widest mb-4">{edu.institution}</p>

                    <div className="flex flex-wrap gap-4 text-[10px] font-black uppercase tracking-widest text-[#0C2C39]/40">
                      <div className="flex items-center space-x-2 bg-[#0C2C39]/5 px-3 py-1 rounded-sm">
                        <Icon name="MapPin" size={12} />
                        <span>{edu.location}</span>
                      </div>
                      <div className="flex items-center space-x-2 bg-[#0C2C39]/5 px-3 py-1 rounded-sm">
                        <Icon name="Calendar" size={12} />
                        <span>{edu.startYear} - {edu.endYear}</span>
                      </div>
                      {edu.gpa && (
                        <div className="flex items-center space-x-2 bg-[#0C2C39]/5 px-3 py-1 rounded-sm text-teal-700">
                          <Icon name="Award" size={12} />
                          <span>GPA: {edu.gpa}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Institution Logo */}
                  <div className="w-20 h-20 bg-white border-2 border-[#0C2C39]/10 rounded-2xl p-3 flex items-center justify-center group-hover:border-[#0C2C39] transition-all duration-300">
                    <Image
                      src={edu.logo}
                      alt={edu.logoAlt}
                      className="w-full h-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                  </div>
                </div>

                {/* Achievements */}
                {edu.achievements.length > 0 && (
                  <div className="pt-6 border-t-2 border-[#0C2C39]/5">
                    <h4 className="text-[10px] font-black text-[#0C2C39]/30 uppercase tracking-[0.3em] mb-4">
                      Academic Milestones
                    </h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {edu.achievements.map((achievement, idx) => (
                        <li key={idx} className="flex items-start space-x-3 text-[#0C2C39]/70 group-hover:text-[#0C2C39]">
                          <div className="mt-1 w-1.5 h-1.5 rounded-full bg-teal-700 flex-shrink-0" />
                          <span className="text-sm font-medium">{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default EducationSection;